import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import subcontractorService from '../../../services/subcontractorService';
import subcontractService from '../../../services/subcontractService';
import { projectService } from '../../../services/projectService';
import { projectCostService } from '../../../services/projectCostService';
import { projectExtrasService } from '../../../services/projectExtrasService';
import paymentRequestService from '../../../services/paymentRequestService';
import { supabase } from '../../../lib/supabase';
import { updateExtractedData, getById as getInboxById, getBestCostCodeFromHistory, recordCostCodeCorrection, deleteInboxItem } from '../../../services/invoiceInboxService';
import { getChatCompletion } from '../../../services/aiIntegrations/chatCompletion';
import expensesService from '../../../services/expensesService';
import { getByProjectId as getDivisionsByProjectId, extractDivisionCode } from '../../../services/divisionsService';
import Icon from '../../../components/AppIcon';
import SubcontractCombobox from '../../../components/ui/SubcontractCombobox';
import SearchableSelect from '../../../components/ui/SearchableSelect';
import GroupedSelect from '../../../components/ui/GroupedSelect';
import useTranslation from '../../../hooks/useTranslation';
import { formatCurrency } from '../../../utils/formatCurrency';
import { formatDate } from '../../../utils/formatDate';

const fmtCAD = (val) => formatCurrency(val);

/**
 * Relit un montant affiche au format quebecois : « 3 382,75 $ » -> 3382.75.
 *
 * parseFloat('3 382,75 $') renvoie 3. L'espace insecable, le symbole et la
 * virgule decimale doivent donc tomber AVANT tout calcul. Un seul parseur pour
 * les six champs de taxe — six regex recopiees, c'est six occasions de diverger.
 *
 * Retourne null quand il n'y a rien d'exploitable, pour distinguer le vide du zero.
 */
const parseAmount = (val) => {
  if (val == null || val === '') return null;
  if (typeof val === 'number') return Number.isFinite(val) ? val : null;
  // \u00A0 espace insecable, \u202F insecable etroite — les deux sortent d'Intl.
  let cleaned = String(val)?.replace(/[\s\u00A0\u202F$]/g, '');
  // « 1,234.56 » : la virgule separe les milliers. « 1234,56 » : elle est decimale.
  cleaned = cleaned?.includes(',') && cleaned?.includes('.')
    ? cleaned?.replace(/,/g, '')
    : cleaned?.replace(',', '.');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
};

// ─── Boite « facture en double » : petits rendus partages ─────
// Une date nue ou un timestamp, lus a midi local pour eviter le decalage
// d'un jour que provoquerait une lecture UTC affichee en heure locale.
const fmtDay = (d, language) => formatDate(d, language);

// `strong` sert au MONTANT : c'est le seul champ qui separe un vrai doublon
// d'une facture revisee portant le meme numero. Il doit sauter aux yeux.
const InfoRow = ({ label, value, strong = false }) => (
  <div className="flex justify-between gap-3 items-baseline">
    <span className="text-gray-500 flex-shrink-0">{label}</span>
    <span
      className={
        strong
          ? 'text-base font-bold text-gray-900 text-right tabular-nums'
          : 'font-medium text-gray-900 text-right break-words min-w-0'
      }
    >
      {value}
    </span>
  </div>
);

// ─── Libelle d'une DP dans un selecteur ───────────────────────
// Toutes les DP sont selectionnables, approuvees comprises : un sous-traitant
// valide son pourcentage AVANT l'approbation et envoie sa facture APRES. Sans
// cela ces factures n'ont nulle part ou atterrir.
// Le statut est un suffixe TEXTE : une <option> native ne rend aucun balisage,
// le texte est donc la seule pastille possible. 'draft' n'en porte aucun.
const dpOptionLabel = (pr, t, prefix) => {
  const suffix =
    pr?.status === 'approved'  ? t(`${prefix}.dpApproved`)
    : pr?.status === 'submitted' ? t(`${prefix}.dpSubmitted`)
    : null;
  return suffix ? `DP${pr?.number} \u2014 ${suffix}` : `DP${pr?.number}`;
};

// ─── Tax rate constants ───────────────────────────────────
const TPS_RATE = 0.05;
const TVQ_RATE = 0.09975;

const INVOICE_TYPES = [
  { value: 'subcontract', label: 'Subcontract' },
  { value: 'extra', label: 'Extra' },
  { value: 'credit', label: 'Credit' },
];

/**
 * AI semantic match: send supplier name + subcontractor list to gpt-4o-mini.
 * Returns { matchedId, matchedName, confidence, reason }
 */
const aiMatchSupplier = async (supplierName, subcontractors) => {
  if (!supplierName || !subcontractors?.length) return null;
  try {
    const subList = subcontractors?.map(s => ({ id: s?.id, name: s?.company_name }));
    const aiResponse = await getChatCompletion(
      'OPEN_AI',
      'gpt-4o-mini',
      [
        {
          role: 'system',
          content: 'You are a subcontractor matching assistant. Match supplier names to the closest company in a list. Return ONLY valid JSON.',
        },
        {
          role: 'user',
          content: `Match this supplier name: "${supplierName}"
to the closest company in this list:
${JSON.stringify(subList, null, 2)}

Return ONLY a JSON object with these exact keys:
{
  "matchedId": "the id of the best match (string) or null if no good match",
  "matchedName": "the company_name of the best match (string) or null",
  "confidence": "a number between 0 and 1 representing match confidence",
  "reason": "brief explanation of the match"
}
Consider abbreviations, typos, French names, trade names vs legal names. If confidence < 0.4, return null for matchedId.`,
        },
      ],
      { max_completion_tokens: 300, temperature: 1 }
    );

    const content = aiResponse?.choices?.[0]?.message?.content || '';
    const cleaned = content?.trim()?.replace(/^```json\s*/i, '')?.replace(/```\s*$/, '')?.trim();
    return JSON.parse(cleaned);
  } catch (err) {
    console.error('AI supplier matching failed:', err);
    return null;
  }
};

/**
 * AI semantic match: match supplier name/trade type against available cost codes.
 * Returns { matchedId, matchedCode, matchedDescription, confidence, reason }
 */
const aiMatchCostCode = async (supplierName, costCodes) => {
  if (!supplierName || !costCodes?.length) return null;
  try {
    const codeList = costCodes?.map(c => ({ id: c?.id, code: c?.costCode, description: c?.description }));
    const aiResponse = await getChatCompletion(
      'OPEN_AI',
      'gpt-4o-mini',
      [
        {
          role: 'system',
          content: 'You are a construction cost code matching assistant. Match a supplier name to the most relevant project cost code based on trade type. Return ONLY valid JSON.',
        },
        {
          role: 'user',
          content: `Given this supplier name: "${supplierName}"

Match it to the most relevant cost code from this list:
${JSON.stringify(codeList, null, 2)}

Infer the trade type from the supplier name (e.g. electrical, concrete, framing, plumbing, HVAC, painting, etc.) and pick the best matching cost code.

Return ONLY a JSON object with these exact keys:
{
  "matchedId": "the id of the best matching cost code (string) or null if no good match",
  "matchedCode": "the cost_code value (string) or null",
  "matchedDescription": "the description of the matched cost code (string) or null",
  "confidence": "a number between 0 and 1 representing match confidence",
  "reason": "brief explanation of why this cost code matches the supplier trade type"
}
If confidence < 0.45, return null for matchedId.`,
        },
      ],
      { max_completion_tokens: 300, temperature: 1 }
    );

    const content = aiResponse?.choices?.[0]?.message?.content || '';
    const cleaned = content?.trim()?.replace(/^```json\s*/i, '')?.replace(/```\s*$/, '')?.trim();
    return JSON.parse(cleaned);
  } catch (err) {
    console.error('AI cost code matching failed:', err);
    return null;
  }
};

/**
 * InvoiceForm — reusable invoice creation form.
 *
 * Props:
 *   initialValues  — object pre-populated from inbox extracted_data:
 *                    { invoiceNumber, invoiceDate, receivedDate, amount, supplier }
 *   onSubmit(payload, lines) — async handler called on "Create Invoice"
 *   onCancel       — called when user clicks Cancel
 *   submitLabel    — button label (default: "Create Invoice")
 *   fileName       — display name shown in the panel header
 *   inboxItemId    — id of the invoice_inbox row (used to persist matching results)
 *   filePath       — storage path of the PDF (used for re-extraction)
 *   inboxItem      — full inbox item object (used for expense routing)
 *   onExpenseCreated — callback() called after expense is successfully created inline
 */
const InvoiceForm = ({
  initialValues = {},
  onSubmit,
  onCancel,
  submitLabel = 'Create Invoice',
  fileName = '',
  inboxItemId = null,
  filePath = null,
  inboxItem = null,
  extractedData = null,
  onPrev = null,
  onNext = null,
  currentIndex = null,
  totalNavigable = 0,
  onItemDeleted = null,
  remainingCount = 0,
  onExpenseCreated = null,
}) => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [subcontractors, setSubcontractors] = useState([]);
  const [subcontracts, setSubcontracts] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [extras, setExtras] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [matchedSubcontractor, setMatchedSubcontractor] = useState(null);
  const [matchedSubcontractorId, setMatchedSubcontractorId] = useState(null);
  const [matchConfidence, setMatchConfidence] = useState(null);
  const [suggestedSubcontractor, setSuggestedSubcontractor] = useState(null);
  const [autoSelected, setAutoSelected] = useState({ subcontractor: false, project: false, costCode: false });
  const [aiMatching, setAiMatching] = useState(false);
  const [aiMatchingCostCode, setAiMatchingCostCode] = useState(false);
  const [hasExtracted, setHasExtracted] = useState(false);
  // Le marqueur en base n'est pas relu pendant la session : cette ref evite un
  // second appel IA au meme code de cout apres un changement de projet.
  const costCodeAttemptRef = useRef(false);

  // ─── Document type: 'invoice' | 'expense' ────────────────
  const [documentType, setDocumentType] = useState(
    inboxItem?.documentType || 'invoice'
  );

  // ─── Expense form state (inline) ─────────────────────────
  const [expenseForm, setExpenseForm] = useState({
    vendor: initialValues?.supplier || '',
    expenseDate: initialValues?.invoiceDate || initialValues?.date || '',
    amount: initialValues?.amount ? String(initialValues?.amount) : '',
    taxAmount: '',
    description: '',
    referenceNumber: initialValues?.invoiceNumber || '',
    status: 'paid',
    divisionSelection: '',
    divisionCode: '',
    costCode: '',
    extraId: '',
    paidBy: '',
    paymentRequestId: initialValues?.paymentRequestId || '',
    taxesIncluded: false,
    expTps: '',
    expTvq: '',
    expTotal: '',
  });
  const [expenseAmountEditing, setExpenseAmountEditing] = useState(false);
  const [expenseTaxEditing, setExpenseTaxEditing] = useState(false);

  // ─── Auto-fill TPS/TVQ/Total when amount or taxesIncluded changes ───
  useEffect(() => {
    if (expenseForm?.taxesIncluded) return; // guard: only runs in OFF mode
    const amt = parseFloat(expenseForm?.amount) || 0;
    if (amt <= 0) {
      setExpenseForm(p => ({ ...p, expTps: '', expTvq: '', expTotal: '' }));
      return;
    }
    const tps   = amt * TPS_RATE;
    const tvq   = amt * TVQ_RATE;
    const total = amt + tps + tvq;
    setExpenseForm(p => ({
      ...p,
      expTps:   tps?.toFixed(2),
      expTvq:   tvq?.toFixed(2),
      expTotal: total?.toFixed(2),
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenseForm?.amount, expenseForm?.taxesIncluded]);

  // ─── Reverse-calc when toggle ON: driver = expTotal ──────
  useEffect(() => {
    if (!expenseForm?.taxesIncluded) return; // guard: only runs in ON mode
    const total = parseAmount(expenseForm?.expTotal) || 0;
    if (total <= 0) {
      setExpenseForm(p => ({ ...p, amount: '', expTps: '', expTvq: '' }));
      return;
    }
    const COMBINED = TPS_RATE + TVQ_RATE; // 0.14975
    const preTax = total / (1 + COMBINED);
    const tps    = preTax * TPS_RATE;
    const tvq    = preTax * TVQ_RATE;
    setExpenseForm(p => ({
      ...p,
      amount: preTax?.toFixed(2),
      expTps: tps?.toFixed(2),
      expTvq: tvq?.toFixed(2),
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenseForm?.expTotal, expenseForm?.taxesIncluded]);

  const [form, setForm] = useState({
    subcontractId: '',
    contractId: '',
    extraId: '',
    creditId: '',
    invoiceNumber: initialValues?.invoiceNumber || '',
    paymentRequestId: '',
    invoiceDate: initialValues?.invoiceDate || initialValues?.date || '',
    receivedDate: initialValues?.receivedDate || '',
    notes: '',
  });

  // Seed a single line with extracted amount if present
  const seedAmount = initialValues?.amount ? String(initialValues?.amount) : '';
  const [lines, setLines] = useState([
    { lineType: 'cost_code', projectCostId: '', extraCreditId: '', description: '', amount: seedAmount, subcontractId: '' },
  ]);
  const [applyTax, setApplyTax] = useState(true);
  const [amountDisplays, setAmountDisplays] = useState({});
  // ─── Invoice tax state (editable, guarded auto-fill) ─────
  const [invTps, setInvTps] = useState('');
  const [invTvq, setInvTvq] = useState('');
  const [invTotal, setInvTotal] = useState('');

  // ─── Montants de taxe : brut en saisie, format quebecois au repos ─────────
  // Meme patron que amountDisplays / expenseAmountEditing plus bas. Un seul
  // champ peut avoir le focus : une seule cle suffit pour les six.
  // L'etat garde TOUJOURS un nombre en clair — le formatage ne vit qu'a l'ecran,
  // sinon parseFloat('3 382,75 $') rendrait 3 dans les totaux.
  const [editingMoney, setEditingMoney] = useState(null);

  const moneyValue = (field, raw) =>
    editingMoney === field
      ? (raw ?? '')
      : (raw !== '' && raw != null ? fmtCAD(raw) : '');

  const commitInvoiceMoney = (raw, setter) => {
    setEditingMoney(null);
    const n = parseAmount(raw);
    setter(n == null ? '' : n?.toFixed(2));
  };

  const commitExpenseMoney = (key) => {
    setEditingMoney(null);
    setExpenseForm(p => {
      const n = parseAmount(p?.[key]);
      return { ...p, [key]: n == null ? '' : n?.toFixed(2) };
    });
  };
  const [retentionIncluded, setRetentionIncluded] = useState(false);
  const [retentionPct, setRetentionPct] = useState('10');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [invoiceType, setInvoiceType] = useState('subcontract');

  // ─── Duplicate detection modals ──────────────────────────
  const [duplicateInvoiceModal, setDuplicateInvoiceModal] = useState(null); // { invoice } | null
  const [duplicateExpenseModal, setDuplicateExpenseModal] = useState(null); // { expense, pendingCallback } | null

  // Re-seed form fields if initialValues changes
  useEffect(() => {
    setForm(prev => ({
      ...prev,
      invoiceNumber: initialValues?.invoiceNumber || '',
      invoiceDate: initialValues?.invoiceDate || initialValues?.date || '',
      receivedDate: initialValues?.receivedDate || '',
    }));
    const newAmount = initialValues?.amount ? String(initialValues?.amount) : '';
    setLines([{ lineType: 'cost_code', projectCostId: '', extraCreditId: '', description: '', amount: newAmount, subcontractId: '' }]);
    setAmountDisplays({});
    setSelectedProjectId('');
    setError('');
    setMatchedSubcontractor(null);
    setMatchedSubcontractorId(null);
    setMatchConfidence(null);
    setSuggestedSubcontractor(null);
    setAutoSelected({ subcontractor: false, project: false, costCode: false });
    setHasExtracted(false);
    costCodeAttemptRef.current = false;
    setDocumentType(inboxItem?.documentType || 'invoice');
    // Pre-fill invoice tax fields from stored values if available (edit mode)
    if (initialValues?.tpsAmount != null) setInvTps(Number(initialValues?.tpsAmount)?.toFixed(2));
    if (initialValues?.tvqAmount != null) setInvTvq(Number(initialValues?.tvqAmount)?.toFixed(2));
    if (initialValues?.totalAmount != null) setInvTotal(Number(initialValues?.totalAmount)?.toFixed(2));
    setRetentionIncluded(initialValues?.retentionIncluded ?? false);
    setRetentionPct(initialValues?.retentionPercent != null ? String(initialValues?.retentionPercent) : '10');
    // Re-seed expense form
    setExpenseForm({
      vendor: initialValues?.supplier || '',
      expenseDate: initialValues?.invoiceDate || initialValues?.date || '',
      amount: initialValues?.amount ? String(initialValues?.amount) : '',
      taxAmount: '',
      description: '',
      referenceNumber: initialValues?.invoiceNumber || '',
      status: 'paid',
      divisionSelection: '',
      divisionCode: '',
      costCode: '',
      extraId: '',
      paidBy: '',
      taxesIncluded: false,
      expTps: '',
      expTvq: '',
      expTotal: '',
    });
  }, [initialValues]);

  // AI semantic matching — runs only once per selected inbox item
  /**
   * Grave la tentative de matching dans extracted_data — succes OU echec.
   *
   * C'est le coeur du correctif : sans marqueur d'echec, un fournisseur que l'IA
   * ne reconnait pas relance un appel payant a chaque ouverture du modal, sans
   * plafond ni trace. Un echec est un resultat, pas une absence de tentative.
   *
   * updateExtractedData fusionne — les 14 champs extraits ne bougent pas.
   */
  const markMatchAttempt = async (key, extra = {}) => {
    if (!inboxItemId) return;
    try {
      await updateExtractedData(inboxItemId, { [key]: new Date()?.toISOString(), ...extra });
    } catch (err) {
      console.error(`Failed to record ${key}:`, err);
    }
  };

  /**
   * Dernier recours pour le code de cout : l'IA.
   * Un seul point de sortie possible sans marqueur — la garde elle-meme.
   */
  const runCostCodeAiMatch = async (supplierName, costData) => {
    const stored = extractedData || inboxItem?.extractedData || {};
    // costCodeSource couvre les factures anterieures au marqueur : pas de migration.
    // La ref couvre la meme session, ou `stored` est encore celui du premier rendu.
    if (costCodeAttemptRef?.current || stored?.costCodeMatchAttemptedAt || stored?.costCodeSource) return;
    costCodeAttemptRef.current = true;

    setAiMatchingCostCode(true);
    try {
      const aiCostMatch = await aiMatchCostCode(supplierName, costData);
      if (aiCostMatch?.matchedId && parseFloat(aiCostMatch?.confidence) >= 0.45) {
        setLines(prev => prev?.map((line, idx) =>
          idx === 0 ? { ...line, projectCostId: aiCostMatch?.matchedId } : line
        ));
        setAutoSelected(prev => ({ ...prev, costCode: true }));
        await markMatchAttempt('costCodeMatchAttemptedAt', {
          suggestedCostCodes: [aiCostMatch?.matchedId],
          costCodeSource: 'ai',
        });
      } else {
        // « Rien trouve » se grave aussi, sinon on repaie a chaque ouverture.
        await markMatchAttempt('costCodeMatchAttemptedAt');
      }
    } catch (err) {
      console.error('AI cost code match error:', err);
      // Une panne reseau ne doit pas relancer l'appel a vie.
      await markMatchAttempt('costCodeMatchAttemptedAt');
    } finally {
      setAiMatchingCostCode(false);
    }
  };

  useEffect(() => {
    if (!initialValues?.supplier || hasExtracted) return;

    const supplierName = initialValues?.supplier;

    // ── Une tentative deja faite ne se refait pas, reussie ou non ──
    // matchedSubcontractorId reste teste : il couvre les factures deja matchees
    // avant l'introduction du marqueur, sans migration.
    const stored = extractedData || inboxItem?.extractedData || {};
    if (stored?.supplierMatchAttemptedAt || stored?.matchedSubcontractorId) {
      setHasExtracted(true);
      // Tentative faite, aucun sous-traitant retenu : rien a appliquer.
      if (!stored?.matchedSubcontractorId) return;
      // Apply stored results without any AI call
      supabase?.from('subcontractors')?.select('*')?.then(({ data: subs }) => {
        if (!subs?.length) return;
        const matchedSub = subs?.find(s => s?.id === stored?.matchedSubcontractorId);
        const confidence = parseFloat(stored?.confidence) || 1;
        if (matchedSub) {
          if (confidence >= 0.8) {
            setMatchedSubcontractor(matchedSub);
            setMatchedSubcontractorId(matchedSub?.id);
            setAutoSelected(prev => ({ ...prev, subcontractor: true }));
          } else {
            setSuggestedSubcontractor({ ...matchedSub, confidence, reason: stored?.reason });
          }
        }
        if (stored?.suggestedProjectId && confidence >= 0.8) {
          setSelectedProjectId(stored?.suggestedProjectId);
          setAutoSelected(prev => ({ ...prev, project: true }));
        }
        if (stored?.suggestedCostCodes?.length > 0) {
          setLines(prev => prev?.map((line, idx) =>
            idx === 0 ? { ...line, projectCostId: stored?.suggestedCostCodes?.[0] } : line
          ));
          setAutoSelected(prev => ({ ...prev, costCode: true }));
        }
      })?.catch(console.error);
      return;
    }

    setHasExtracted(true);
    setAiMatching(true);

    supabase?.from('subcontractors')?.select('*')?.then(async ({ data: subs }) => {
      if (!subs?.length) { setAiMatching(false); return; }

      // Use AI semantic matching
      const aiMatch = await aiMatchSupplier(supplierName, subs);
      setAiMatching(false);

      // Aucun rapprochement : l'appel a bien eu lieu, il doit laisser sa trace.
      if (!aiMatch?.matchedId) { await markMatchAttempt('supplierMatchAttemptedAt'); return; }

      const confidence = parseFloat(aiMatch?.confidence) || 0;
      setMatchConfidence(confidence);

      const matchedSub = subs?.find(s => s?.id === aiMatch?.matchedId);
      // L'IA a repondu un id inconnu de la liste : tentative faite quand meme.
      if (!matchedSub) { await markMatchAttempt('supplierMatchAttemptedAt'); return; }

      if (confidence >= 0.8) {
        // High confidence: auto-select
        setMatchedSubcontractor(matchedSub);
        setMatchedSubcontractorId(matchedSub?.id);
        setAutoSelected(prev => ({ ...prev, subcontractor: true }));
      } else {
        // Lower confidence: suggest only
        setSuggestedSubcontractor({ ...matchedSub, confidence, reason: aiMatch?.reason });
      }

      // Query sub_invoices history to suggest most recent project
      const { data: invoices } = await supabase
        ?.from('sub_invoices')
        ?.select('project_id, created_at')
        ?.eq('subcontractor_id', matchedSub?.id)
        ?.order('created_at', { ascending: false });

      const suggestedProject = invoices?.[0]?.project_id;
      if (suggestedProject && confidence >= 0.8) {
        setSelectedProjectId(suggestedProject);
        setAutoSelected(prev => ({ ...prev, project: true }));
      }

      // Persist matching results into extracted_data — all camelCase
      if (inboxItemId) {
        try {
          await updateExtractedData(inboxItemId, {
            supplier: supplierName,
            matchedSubcontractorId: matchedSub?.id,
            suggestedProjectId: suggestedProject || null,
            suggestedCostCodes: [],
            confidence,
            supplierMatchAttemptedAt: new Date()?.toISOString(),
          });

          // Fetch updated row and apply results to form
          const updated = await getInboxById(inboxItemId);
          const data = updated?.extractedData || {};
          console.log('AI MATCH RESULT:', data);

          if (data?.matchedSubcontractorId && confidence >= 0.8) {
            setMatchedSubcontractorId(data?.matchedSubcontractorId);
            setAutoSelected(prev => ({ ...prev, subcontractor: true }));
          }
          if (data?.suggestedProjectId && confidence >= 0.8) {
            setSelectedProjectId(data?.suggestedProjectId);
            setAutoSelected(prev => ({ ...prev, project: true }));
          }
          if (data?.suggestedCostCodes?.length > 0) {
            setLines(prev => prev?.map((line, idx) =>
              idx === 0 ? { ...line, projectCostId: data?.suggestedCostCodes?.[0] } : line
            ));
            setAutoSelected(prev => ({ ...prev, costCode: true }));
          }
        } catch (err) {
          console.error('updateExtractedData error:', err);
          // L'ecriture des resultats a echoue : au minimum, graver la tentative.
          await markMatchAttempt('supplierMatchAttemptedAt');
        }
      }
    })?.catch(async (err) => {
      console.error(err);
      setAiMatching(false);
      // Panne au milieu du matching : sans marqueur, on relance a chaque ouverture.
      await markMatchAttempt('supplierMatchAttemptedAt');
    });
  }, [initialValues, hasExtracted]);

  // Load projects on mount
  useEffect(() => {
    projectService?.getAll()?.then(data => setProjects(data || []))?.catch(console.error);
  }, []);

  // Load project-specific data when project changes
  useEffect(() => {
    if (!selectedProjectId) {
      setSubcontractors([]);
      setSubcontracts([]);
      setCostCodes([]);
      setExtras([]);
      setPaymentRequests([]);
      return;
    }
    setProjectLoading(true);
    Promise.all([
      subcontractorService?.getAll ? subcontractorService?.getAll() : Promise.resolve([]),
      projectCostService?.getByProjectId(selectedProjectId),
      projectExtrasService?.getByProjectId(selectedProjectId),
      subcontractService?.getByProjectId(selectedProjectId),
      paymentRequestService?.getByProjectId(selectedProjectId),
      getDivisionsByProjectId(selectedProjectId),
    ])
      ?.then(([subData, costData, extData, subcontractData, prData, divData]) => {
        setSubcontractors(subData || []);
        setCostCodes(costData || []);
        setExtras(extData || []);
        setSubcontracts(subcontractData || []);
        setPaymentRequests(prData || []);
        setDivisions(divData || []);

        // Auto-select subcontract belonging to matched subcontractor
        if (matchedSubcontractorId && subcontractData?.length) {
          const matchingSubcontract = subcontractData?.find(
            s => s?.subcontractorId === matchedSubcontractorId
          );
          if (matchingSubcontract) {
            setForm(prev => ({
              ...prev,
              subcontractId: matchingSubcontract?.id,
              contractId: matchingSubcontract?.id,
            }));

            // ── TRAINING LAYER: check corrections table first ──────────────
            const supplierName = initialValues?.supplier;
            getBestCostCodeFromHistory(supplierName, selectedProjectId)
              ?.then(async (trainingMatch) => {
                if (trainingMatch?.projectCostId) {
                  // Training data found — use it directly (highest priority)
                  setLines(prev => prev?.map((line, idx) =>
                    idx === 0 ? { ...line, projectCostId: trainingMatch?.projectCostId } : line
                  ));
                  setAutoSelected(prev => ({ ...prev, costCode: true }));
                  await markMatchAttempt('costCodeMatchAttemptedAt', {
                    suggestedCostCodes: [trainingMatch?.projectCostId],
                    costCodeSource: 'training',
                  });
                  return; // Skip historical frequency + AI matching
                }

                // ── FALLBACK: historical frequency from sub_invoice_lines ──
                const { data: lineHistory } = await supabase
                  ?.from('sub_invoice_lines')
                  ?.select('project_cost_id')
                  ?.eq('subcontract_id', matchingSubcontract?.id);

                let historicalCostCodeId = null;
                if (lineHistory?.length) {
                  const freq = {};
                  lineHistory?.forEach(l => {
                    if (l?.project_cost_id) {
                      freq[l.project_cost_id] = (freq?.[l?.project_cost_id] || 0) + 1;
                    }
                  });
                  historicalCostCodeId = Object.entries(freq)?.sort((a, b) => b?.[1] - a?.[1])?.[0]?.[0];
                }

                if (historicalCostCodeId) {
                  setLines(prev => prev?.map((line, idx) =>
                    idx === 0 ? { ...line, projectCostId: historicalCostCodeId } : line
                  ));
                  setAutoSelected(prev => ({ ...prev, costCode: true }));
                  await markMatchAttempt('costCodeMatchAttemptedAt', {
                    suggestedCostCodes: [historicalCostCodeId],
                    costCodeSource: 'frequency',
                  });
                } else if (supplierName && costData?.length) {
                  // ── LAST RESORT: AI trade-type matching ──────────────────
                  await runCostCodeAiMatch(supplierName, costData);
                }
              })
              ?.catch(console.error);
          } else if (initialValues?.supplier && costData?.length) {
            // No matching subcontract — check training table then AI
            const supplierName = initialValues?.supplier;
            getBestCostCodeFromHistory(supplierName, selectedProjectId)
              ?.then(async (trainingMatch) => {
                if (trainingMatch?.projectCostId) {
                  setLines(prev => prev?.map((line, idx) =>
                    idx === 0 ? { ...line, projectCostId: trainingMatch?.projectCostId } : line
                  ));
                  setAutoSelected(prev => ({ ...prev, costCode: true }));
                  await markMatchAttempt('costCodeMatchAttemptedAt', {
                    suggestedCostCodes: [trainingMatch?.projectCostId],
                    costCodeSource: 'training',
                  });
                  return;
                }
                // Fall back to AI
                await runCostCodeAiMatch(supplierName, costData);
              })
              ?.catch(console.error);
          }
        } else if (initialValues?.supplier && costData?.length) {
          // No matched subcontractor at all — check training table then AI
          const supplierName = initialValues?.supplier;
          getBestCostCodeFromHistory(supplierName, selectedProjectId)
            ?.then(async (trainingMatch) => {
              if (trainingMatch?.projectCostId) {
                setLines(prev => prev?.map((line, idx) =>
                  idx === 0 ? { ...line, projectCostId: trainingMatch?.projectCostId } : line
                ));
                setAutoSelected(prev => ({ ...prev, costCode: true }));
                await markMatchAttempt('costCodeMatchAttemptedAt', {
                  suggestedCostCodes: [trainingMatch?.projectCostId],
                  costCodeSource: 'training',
                });
                return;
              }
              // Fall back to AI
              await runCostCodeAiMatch(supplierName, costData);
            })
            ?.catch(console.error);
        }
      })
      ?.catch(console.error)
      ?.finally(() => setProjectLoading(false));
  }, [selectedProjectId, matchedSubcontractorId]);

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const addLine = () => setLines(p => [...p, { lineType: 'cost_code', projectCostId: '', extraCreditId: '', description: '', amount: '', subcontractId: '' }]);
  const removeLine = (i) => setLines(p => p?.filter((_, idx) => idx !== i));
  const setLine = (i, k, v) => setLines(p => p?.map((l, idx) => idx === i ? { ...l, [k]: v } : l));

  const handleAmountFocus = (i) => setAmountDisplays(prev => ({ ...prev, [i]: 'editing' }));
  const handleAmountBlur = (i) => setAmountDisplays(prev => ({ ...prev, [i]: 'formatted' }));

  const extraOptions = extras?.filter(e => e?.type === 'extra') || [];
  const creditOptions = extras?.filter(e => e?.type === 'credit') || [];

  const getLaborMaterialSubcontracts = (projectCostId) => {
    if (!projectCostId) return [];
    return (subcontracts || [])?.filter(s => s?.projectCostId === projectCostId && s?.pricingModel === 'labor_material');
  };

  const lineTotal = lines?.reduce((s, l) => s + (parseFloat(l?.amount) || 0), 0);
  const subtotal = lineTotal;

  // ─── Invoice tax: guarded useEffect on [subtotal, applyTax] ONLY ───────────
  // Taxes are always computed on the FULL amount (no retention reduction here).
  // Non-driver fields (invTps, invTvq, invTotal) are NOT in deps so manual
  // corrections survive until lines (subtotal) or the tax toggle changes.
  useEffect(() => {
    if (applyTax) {
      const tpsVal = subtotal * TPS_RATE;
      const tvqVal = subtotal * TVQ_RATE;
      setInvTps(tpsVal?.toFixed(2));
      setInvTvq(tvqVal?.toFixed(2));
      setInvTotal((subtotal + tpsVal + tvqVal)?.toFixed(2));
    } else {
      setInvTps('0.00');
      setInvTvq('0.00');
      setInvTotal(subtotal?.toFixed(2));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal, applyTax]);

  // ─── Retention module (applied at intake) ───────────────────────────────────
  // "Montant approuvé (100%)" = full tax-inclusive total (editable invTotal).
  // When "Appliquer la retenue" is checked, the SAVED total_amount becomes the
  // Net à payer; subtotal/tps/tvq stay FULL (pre-retention).
  const fullTotal = parseAmount(invTotal) || 0;
  const retentionPctNum = retentionIncluded ? Math.max(0, parseFloat(retentionPct) || 0) : 0;
  const retentionAmount = retentionIncluded ? Math.round(fullTotal * retentionPctNum / 100 * 100) / 100 : 0;
  const netPayable = Math.round((fullTotal - retentionAmount) * 100) / 100;

  /**
   * La facture existante qui entre en collision.
   *
   * MEMES trois cles que la regle de detection — project_id + subcontractor_id
   * + invoice_number — la regle ne change pas. On lui demande seulement PLUS DE
   * COLONNES : le montant, le sous-traitant et le projet, absents jusqu'ici,
   * sans lesquels personne ne peut juger avant de detruire un document.
   *
   * Une seule definition, partagee par le controle preventif et par le filet
   * de l'index unique : les deux boites montrent donc la meme chose.
   */
  const findExistingInvoice = async (projectId, subcontractorId, invoiceNumber, excludeId) => {
    if (!projectId || !subcontractorId || !invoiceNumber) return null;
    let query = supabase
      ?.from('sub_invoices')
      ?.select(`
        id, invoice_number, invoice_date, status, created_at,
        total_amount, subtotal_amount,
        subcontractor:subcontractors ( company_name ),
        project:projects ( name, project_number )
      `)
      ?.eq('project_id', projectId)
      ?.eq('subcontractor_id', subcontractorId)
      ?.eq('invoice_number', invoiceNumber)
      ?.limit(1);
    if (excludeId) query = query?.neq('id', excludeId);
    const { data, error: lookupError } = await query;
    if (lookupError) throw lookupError;
    return data?.[0] || null;
  };

  const handleSave = async () => {
    setError('');

    // ─── Expense inline form ──────────────────────────────
    if (documentType === 'expense') {
      if (!selectedProjectId) { setError(t('invoiceInbox.form.errors.selectProject')); return; }
      if (!expenseForm?.vendor?.trim()) { setError(t('invoiceInbox.form.errors.enterVendor')); return; }
      if (!expenseForm?.amount || parseFloat(expenseForm?.amount) <= 0) { setError(t('invoiceInbox.form.errors.enterAmount')); return; }
      if (!expenseForm?.paymentRequestId) { setError(t('invoiceInbox.form.errors.paymentRequestRequired')); return; }

      // ── SOFT DUPLICATE CHECK (expense) ──────────────────
      if (expenseForm?.referenceNumber?.trim() && expenseForm?.vendor?.trim()) {
        try {
          const { data: existingExpenses } = await supabase
            ?.from('project_expenses')
            ?.select('id, reference_number, vendor, amount, expense_date, created_at')
            ?.eq('project_id', selectedProjectId)
            ?.ilike('vendor', expenseForm?.vendor?.trim())
            ?.eq('reference_number', expenseForm?.referenceNumber?.trim())
            ?.limit(1);

          if (existingExpenses?.length > 0) {
            // Show soft warning modal — user can cancel or save anyway
            setDuplicateExpenseModal({
              expense: existingExpenses?.[0],
              pendingCallback: null, // will be set to proceed
            });
            return; // pause — wait for user choice
          }
        } catch (checkErr) {
          console.error('[handleSave] expense dedup check failed:', checkErr);
          // Non-blocking: continue if check fails
        }
      }

      await _doCreateExpense();
      return;
    }

    // ─── Invoice validation ───────────────────────────────
    if (!selectedProjectId) { setError(t('invoiceInbox.form.errors.selectProject')); return; }
    if (!form?.subcontractId) { setError(t('invoiceInbox.form.errors.selectSubcontract')); return; }
    if (invoiceType === 'subcontract' && !form?.subcontractId) { setError(t('invoiceInbox.form.errors.selectSubcontract')); return; }
    if (invoiceType === 'extra' && !form?.extraId) { setError(t('invoiceInbox.form.errors.selectExtra')); return; }
    if (invoiceType === 'credit' && !form?.creditId) { setError(t('invoiceInbox.form.errors.selectCredit')); return; }
    if (!form?.paymentRequestId) { setError(t('invoiceInbox.form.errors.paymentRequestRequired')); return; }
    if (lines?.length === 0) { setError(t('invoiceInbox.form.errors.addLine')); return; }
    for (const l of lines) {
      if (!parseFloat(l?.amount) || parseFloat(l?.amount) <= 0) { setError(t('invoiceInbox.form.errors.lineAmount')); return; }
      if (l?.lineType === 'cost_code' && !l?.projectCostId) { setError(t('invoiceInbox.form.errors.lineCostCode')); return; }
      if (l?.lineType === 'extra_credit' && !l?.extraCreditId) { setError(t('invoiceInbox.form.errors.lineExtraCredit')); return; }
    }

    // ── HARD DUPLICATE CHECK (invoice) ──────────────────
    if (form?.invoiceNumber?.trim()) {
      try {
        const selectedSubcontract = subcontracts?.find(s => s?.id === form?.subcontractId);
        const subcontractorId = selectedSubcontract?.subcontractorId || null;

        if (subcontractorId) {
          const existing = await findExistingInvoice(
            selectedProjectId,
            subcontractorId,
            form?.invoiceNumber?.trim(),
            form?.id || null
          );

          if (existing) {
            // Hard block — show modal, no force option
            setDuplicateInvoiceModal({ invoice: existing });
            return; // BLOCKED — do not call create
          }
        }
      } catch (checkErr) {
        console.error('[handleSave] invoice dedup check failed:', checkErr);
        // Non-blocking: continue if check fails
      }
    }

    setSaving(true);
    try {
      const selectedSubcontract = subcontracts?.find(s => s?.id === form?.subcontractId);
      const payload = {
        ...form,
        projectId: selectedProjectId,
        subcontractId: form?.subcontractId || null,
        contractId: invoiceType === 'subcontract' ? form?.subcontractId : null,
        extraId: invoiceType === 'extra' ? form?.extraId : null,
        creditId: invoiceType === 'credit' ? form?.creditId : null,
        paymentRequestId: form?.paymentRequestId || null,
        subcontractorId: selectedSubcontract?.subcontractorId || null,
        // Tax summary fields (editable, user-correctable) — always FULL / pre-retention
        subtotalAmount: subtotal,
        tpsAmount: parseAmount(invTps) || 0,
        tvqAmount: parseAmount(invTvq) || 0,
        // total_amount is ALWAYS the full tax-inclusive total (brut). Retention lives in
        // retention_included + retention_percent; the net payable is derived downstream.
        totalAmount: parseAmount(invTotal) || 0,
        taxApplied: applyTax,
        retentionIncluded: retentionIncluded,
        retentionPercent: retentionIncluded ? retentionPctNum : null,
      };
      await onSubmit?.(payload, lines);

      // ─── TRAINING SIGNAL: record confirmation/override for each cost-code line ──
      if (initialValues?.supplier && selectedProjectId) {
        const storedSuggestion = (extractedData || inboxItem?.extractedData)?.suggestedCostCodes?.[0] || null;
        for (const line of lines) {
          if (line?.lineType === 'cost_code' && line?.projectCostId) {
            const wasOverride = storedSuggestion && storedSuggestion !== line?.projectCostId;
            recordCostCodeCorrection({
              supplierName: initialValues?.supplier,
              projectId: selectedProjectId,
              subcontractId: form?.subcontractId || null,
              projectCostId: line?.projectCostId,
              wasOverride: !!wasOverride,
              originalAiSuggestion: wasOverride ? storedSuggestion : null,
            })?.catch(console.error);
          }
        }
      }
    } catch (err) {
      // ── Filet DB : contrainte unique (race condition) ──
      if (err?.code === 'DUPLICATE_INVOICE') {
        // Ne jamais ouvrir la boite a vide : on va chercher la facture
        // existante avec les MEMES trois cles que la regle de detection,
        // pour que le panneau de gauche ait quelque chose a montrer.
        let existing = null;
        try {
          const sc = subcontracts?.find(s => s?.id === form?.subcontractId);
          existing = await findExistingInvoice(
            selectedProjectId,
            sc?.subcontractorId || null,
            form?.invoiceNumber?.trim(),
            form?.id || null
          );
        } catch (lookupErr) {
          console.error('[handleSave] existing invoice lookup failed:', lookupErr);
        }
        setDuplicateInvoiceModal({ invoice: existing });
      } else {
        setError(err?.message || t('invoiceInbox.form.errors.createInvoice'));
      }
    } finally {
      setSaving(false);
    }
  };

  // ─── Internal helper: actually create the expense ────────
  const _doCreateExpense = async () => {
    setSaving(true);
    try {
      // Persist document type choice
      if (inboxItemId) {
        try {
          const { setDocumentType: saveDocType } = await import('../../../services/invoiceInboxService');
          await saveDocType(inboxItemId, 'expense');
        } catch (err) {
          console.error('Failed to save document type:', err);
        }
      }

      // Use the current field values (user may have manually corrected them).
      // amount (pre-tax) = total − taxes, so pre-tax + taxes always reconcile to total.
      const expTpsVal    = parseAmount(expenseForm?.expTps) || 0;
      const expTvqVal    = parseAmount(expenseForm?.expTvq) || 0;
      const expTotalVal  = parseAmount(expenseForm?.expTotal) || 0;
      const expTaxAmount = parseFloat((expTpsVal + expTvqVal)?.toFixed(2));
      const expAmt       = parseFloat((expTotalVal - expTaxAmount)?.toFixed(2));

      // Decode division selection to get final cost_code and division_code
      const [selCat, selCode] = (expenseForm?.divisionSelection || '')?.split('::');
      const finalCostCode = expenseForm?.costCode || null;
      const finalDivisionCode = selCat === 'construction' ? selCode : (selCode || expenseForm?.divisionCode || null);

      const newExpense = await expensesService?.create({
        projectId: selectedProjectId,
        vendor: expenseForm?.vendor?.trim(),
        expenseDate: expenseForm?.expenseDate || null,
        amount: expAmt,
        taxAmount: expTaxAmount,
        description: expenseForm?.description?.trim() || null,
        referenceNumber: expenseForm?.referenceNumber?.trim() || null,
        status: expenseForm?.status || 'paid',
        divisionCode: finalDivisionCode,
        costCode: finalCostCode,
        extraId: expenseForm?.extraId || null,
        paidBy: expenseForm?.paidBy || null,
        paymentRequestId: expenseForm?.paymentRequestId || null,
      });

      // ─── Copy source PDF to contracts-documents so it appears in Documents modal ───
      if (newExpense?.id && filePath) {
        try {
          const { data: fileData, error: downloadErr } = await supabase?.storage
            ?.from('invoice-inbox')
            ?.download(filePath);
          if (!downloadErr && fileData) {
            const sourceFileName = filePath?.split('/')?.pop() || fileName || 'document.pdf';
            const destPath = `expense/${newExpense?.id}/${sourceFileName}`;
            await supabase?.storage
              ?.from('contracts-documents')
              ?.upload(destPath, fileData, { upsert: true, cacheControl: '3600' });
          }
        } catch (copyErr) {
          console.error('Failed to copy inbox file to expense documents:', copyErr);
        }
      }

      await onExpenseCreated?.(newExpense?.id);
      onCancel?.();
    } catch (err) {
      setError(err?.message || t('invoiceInbox.form.errors.createExpense'));
    } finally {
      setSaving(false);
    }
  };

  const confidencePct = matchConfidence !== null ? Math.round(matchConfidence * 100) : null;

  // ─── Duplicate Invoice Modal (hard block) ────────────────
  const DuplicateInvoiceModalComponent = () => {
    if (!duplicateInvoiceModal) return null;
    const inv = duplicateInvoiceModal?.invoice;

    // Montant de la facture existante : TTC si connu, sinon le sous-total.
    // On garde le null distinct de zero — formatCurrency rendrait « 0,00 $ »,
    // indiscernable d'une facture reellement a zero.
    const existingAmount =
      inv?.total_amount != null ? inv?.total_amount
      : inv?.subtotal_amount != null ? inv?.subtotal_amount
      : null;

    const existingProjectLabel = inv?.project
      ? [inv?.project?.project_number, inv?.project?.name]?.filter(Boolean)?.join(' \u2014 ') || '—'
      : '—';

    // Montant en cours de saisie : le total TTC si l'utilisateur l'a rempli,
    // sinon la somme des lignes.
    const typedTotal = parseAmount(invTotal);
    const incomingAmount = Number.isFinite(typedTotal) && typedTotal > 0 ? typedTotal : subtotal;

    const incomingSender =
      inboxItem?.senderEmail
      || inboxItem?.uploader?.full_name
      || inboxItem?.uploader?.email
      || '—';

    const handleDelete = async () => {
      // L'erreur vit dans l'etat de la modale : elle reste ouverte, et la ligne
      // n'est retiree de la liste QUE si la base a confirme la suppression.
      setDuplicateInvoiceModal((prev) => ({ ...prev, deleting: true, error: null }));
      try {
        await deleteInboxItem(inboxItemId, filePath);
        setDuplicateInvoiceModal(null);
        onItemDeleted?.();
      } catch (err) {
        console.error('[DuplicateInvoiceModal] delete failed:', err);
        setDuplicateInvoiceModal((prev) => ({
          ...prev,
          deleting: false,
          error:
            err?.code === 'INBOX_DELETE_NOT_PERMITTED'
              ? t('invoiceInbox.deleteErrors.notPermitted')
              : t('invoiceInbox.deleteErrors.failed'),
        }));
      }
    };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <Icon name="AlertCircle" size={20} className="text-red-600" />
            </div>
            <h2 className="text-base font-semibold text-gray-900">{t('duplicateInvoiceModal.title')}</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">{t('duplicateInvoiceModal.message')}</p>
          {/* Deux objets DIFFERENTS, cote a cote. A gauche ce qui existe deja
              en base ; a droite le document recu, celui que le bouton detruit.
              La confusion entre les deux etait le piege de l'ancienne boite. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">

            {/* GAUCHE — la facture deja enregistree */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                {t('duplicateInvoiceModal.existingLabel')}
              </p>
              {inv ? (
                <div className="space-y-1 text-sm">
                  <InfoRow label={t('duplicateInvoiceModal.invoiceNumber')} value={inv?.invoice_number || '—'} />
                  <InfoRow label={t('duplicateInvoiceModal.date')} value={fmtDay(inv?.invoice_date, language)} />
                  <InfoRow
                    label={t('duplicateInvoiceModal.amount')}
                    value={existingAmount != null ? fmtCAD(existingAmount) : '—'}
                    strong
                  />
                  <InfoRow label={t('duplicateInvoiceModal.subcontractor')} value={inv?.subcontractor?.company_name || '—'} />
                  <InfoRow label={t('duplicateInvoiceModal.project')} value={existingProjectLabel} />
                  <InfoRow
                    label={t('duplicateInvoiceModal.status')}
                    value={inv?.status ? t(`subInvoices.status.${inv?.status}`) : '—'}
                  />
                  <InfoRow label={t('duplicateInvoiceModal.createdAt')} value={fmtDay(inv?.created_at, language)} />
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  {t('duplicateInvoiceModal.existingNotLoaded')}
                </p>
              )}
            </div>

            {/* DROITE — le document recu, celui qui va etre detruit */}
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">
                {t('duplicateInvoiceModal.incomingLabel')}
              </p>
              <div className="space-y-1 text-sm">
                <InfoRow label={t('duplicateInvoiceModal.fileLabel')} value={fileName || '—'} />
                <InfoRow label={t('duplicateInvoiceModal.receivedFrom')} value={incomingSender} />
                <InfoRow label={t('duplicateInvoiceModal.receivedAt')} value={fmtDay(inboxItem?.createdAt, language)} />
                <InfoRow label={t('duplicateInvoiceModal.invoiceNumber')} value={form?.invoiceNumber?.trim() || '—'} />
                <InfoRow
                  label={t('duplicateInvoiceModal.amount')}
                  value={incomingAmount > 0 ? fmtCAD(incomingAmount) : '—'}
                  strong
                />
              </div>
            </div>
          </div>
          {duplicateInvoiceModal?.error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
              <Icon name="AlertCircle" size={15} className="mt-0.5 flex-shrink-0" />
              <span>{duplicateInvoiceModal?.error}</span>
            </div>
          )}
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setDuplicateInvoiceModal(null)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('duplicateInvoiceModal.cancel')}
            </button>
            <button
              onClick={handleDelete}
              disabled={duplicateInvoiceModal?.deleting}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60"
            >
              {duplicateInvoiceModal?.deleting && (
                <Icon name="Loader2" size={13} className="animate-spin" />
              )}
              {t('duplicateInvoiceModal.delete')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Duplicate Expense Modal (soft warning) ──────────────
  const DuplicateExpenseModalComponent = () => {
    if (!duplicateExpenseModal) return null;
    const exp = duplicateExpenseModal?.expense;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} className="text-amber-600" />
            </div>
            <h2 className="text-base font-semibold text-gray-900">{t('duplicateExpenseModal.title')}</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">{t('duplicateExpenseModal.message')}</p>
          {exp && (
            <div className="bg-gray-50 rounded-lg p-3 mb-5 text-sm space-y-1">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t('duplicateExpenseModal.existingLabel')}</p>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('duplicateExpenseModal.invoiceNumber')}</span>
                <span className="font-medium text-gray-900">{exp?.reference_number || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('duplicateExpenseModal.vendor')}</span>
                <span className="font-medium text-gray-900">{exp?.vendor || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('duplicateExpenseModal.amount')}</span>
                <span className="font-medium text-gray-900">{exp?.amount != null ? fmtCAD(exp?.amount) : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('duplicateExpenseModal.date')}</span>
                <span className="font-medium text-gray-900">{exp?.expense_date || '—'}</span>
              </div>
            </div>
          )}
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setDuplicateExpenseModal(null)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('duplicateExpenseModal.cancel')}
            </button>
            <button
              onClick={async () => {
                setDuplicateExpenseModal(null);
                await _doCreateExpense();
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
            >
              {t('duplicateExpenseModal.saveAnyway')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Duplicate detection modals */}
      <DuplicateInvoiceModalComponent />
      <DuplicateExpenseModalComponent />
      {/* Panel Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{t('invoiceInbox.form.panelTitle')}</h3>
          {fileName && (
            <p className="text-xs text-gray-500 truncate max-w-[220px]">{fileName}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Prev / Next navigation */}
          {(onPrev || onNext) && (
            <div className="flex items-center gap-1 mr-1">
              <button
                onClick={onPrev}
                disabled={!onPrev}
                title={t('invoiceInbox.form.prevInvoice')}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="ChevronLeft" size={15} />
              </button>
              {currentIndex !== null && (
                <span className="text-xs text-gray-500 whitespace-nowrap px-1">
                  <span className="font-semibold text-gray-700">{currentIndex}</span>
                  <span className="text-gray-400"> / {totalNavigable}</span>
                  {remainingCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-600 text-[10px] font-semibold">
                      {t('invoiceInbox.form.remainingLeft', { count: remainingCount })}
                    </span>
                  )}
                </span>
              )}
              <button
                onClick={onNext}
                disabled={!onNext}
                title={t('invoiceInbox.form.nextInvoice')}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="ChevronRight" size={15} />
              </button>
            </div>
          )}
          {onCancel && (
            <button onClick={onCancel} className="p-1 rounded text-gray-400 hover:text-gray-600">
              <Icon name="X" size={16} />
            </button>
          )}
        </div>
      </div>
      {/* Scrollable Form Body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
        )}

        {/* AI matching in progress */}
        {aiMatching && (
          <div className="flex items-center gap-2 p-3 bg-purple-50 border border-purple-100 rounded-lg">
            <Icon name="Loader2" size={14} className="text-purple-500 animate-spin flex-shrink-0" />
            <p className="text-xs text-purple-700">{t('invoiceInbox.form.ai.matchingSupplier')}</p>
          </div>
        )}

        {/* AI cost code matching in progress */}
        {aiMatchingCostCode && (
          <div className="flex items-center gap-2 p-3 bg-indigo-50 border border-indigo-100 rounded-lg">
            <Icon name="Loader2" size={14} className="text-indigo-500 animate-spin flex-shrink-0" />
            <p className="text-xs text-indigo-700">{t('invoiceInbox.form.ai.matchingCostCode')}</p>
          </div>
        )}

        {/* Extracted data hint */}
        {(initialValues?.supplier || initialValues?.amount) && (
          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
            <Icon name="Sparkles" size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-blue-700 space-y-0.5">
              {initialValues?.supplier && <p><span className="font-medium">{t('invoiceInbox.form.ai.supplierLabel')}</span> {initialValues?.supplier}</p>}
              {matchedSubcontractor && (
                <p className="flex items-center gap-1">
                  <Icon name="CheckCircle" size={12} className="text-green-500 flex-shrink-0" />
                  <span className="font-medium text-green-700">{t('invoiceInbox.form.ai.matchedLabel')}</span>
                  <span className="text-green-700">{matchedSubcontractor?.company_name}</span>
                  {confidencePct !== null && (
                    <span className="ml-1 text-green-600 font-semibold">{t('invoiceInbox.form.ai.confidenceLabel', { pct: confidencePct })}</span>
                  )}
                </p>
              )}
              {/* Low-confidence suggestion */}
              {suggestedSubcontractor && !matchedSubcontractor && (
                <div className="flex items-start gap-1.5 mt-1 p-2 bg-amber-50 border border-amber-200 rounded-md">
                  <Icon name="AlertCircle" size={12} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-700 font-medium">{t('invoiceInbox.form.ai.possibleMatch', { pct: Math.round((suggestedSubcontractor?.confidence || 0) * 100) })}</p>
                    <p className="text-amber-700">{suggestedSubcontractor?.company_name}</p>
                    <p className="text-amber-500 text-[10px]">{suggestedSubcontractor?.reason}</p>
                    <button
                      onClick={() => {
                        setMatchedSubcontractor(suggestedSubcontractor);
                        setMatchedSubcontractorId(suggestedSubcontractor?.id);
                        setMatchConfidence(suggestedSubcontractor?.confidence);
                        setAutoSelected(prev => ({ ...prev, subcontractor: true }));
                        setSuggestedSubcontractor(null);
                      }}
                      className="mt-1 text-[10px] font-medium text-amber-700 underline hover:text-amber-900"
                    >
                      {t('invoiceInbox.form.ai.acceptMatch')}
                    </button>
                  </div>
                </div>
              )}
              {initialValues?.amount && <p><span className="font-medium">{t('invoiceInbox.form.ai.amountDetected')}</span> {fmtCAD(initialValues?.amount)}</p>}
              <p className="text-blue-500">{t('invoiceInbox.form.ai.fieldsPreFilled')}</p>
            </div>
          </div>
        )}

        {/* Project + Document Type row */}
        <div className="flex items-end gap-2">
          {/* Project Selector */}
          <div className="flex-1 min-w-0">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('invoiceInbox.form.project.label')} <span className="text-red-500">*</span>
              {autoSelected?.project && (
                <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                  <Icon name="Sparkles" size={10} />
                  {t('invoiceInbox.form.project.autoSelected')}
                </span>
              )}
            </label>
            <SearchableSelect
              options={[...( projects || [])]
                ?.sort((a, b) => {
                  if (a?.createdAt && b?.createdAt) return new Date(b?.createdAt) - new Date(a?.createdAt);
                  const pnA = a?.projectNumber || '';
                  const pnB = b?.projectNumber || '';
                  return pnB?.localeCompare(pnA, undefined, { numeric: true });
                })
                ?.map(p => ({
                  value: p?.id,
                  label: p?.projectNumber ? `${p?.projectNumber} - ${p?.name || p?.projectName || p?.id}` : (p?.name || p?.projectName || p?.id),
                }))}
              value={selectedProjectId}
              onChange={val => { setSelectedProjectId(val || ''); setAutoSelected(prev => ({ ...prev, project: false })); }}
              placeholder={t('invoiceInbox.form.project.placeholder')}
              size="sm"
            />
          </div>

          {/* Document Type Toggle */}
          <div className="flex-shrink-0">
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoiceInbox.form.typeToggle.label')}</label>
            <div className="flex border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setDocumentType('invoice')}
                className={`flex items-center gap-1 px-3 py-2 text-xs font-medium transition-colors ${
                  documentType === 'invoice' ?'bg-primary text-white' :'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Icon name="FileText" size={12} />
                {t('invoiceInbox.form.typeToggle.invoice')}
              </button>
              <button
                type="button"
                onClick={() => setDocumentType('expense')}
                className={`flex items-center gap-1 px-3 py-2 text-xs font-medium transition-colors ${
                  documentType === 'expense' ?'bg-blue-500 text-white' :'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Icon name="Receipt" size={12} />
                {t('invoiceInbox.form.typeToggle.expense')}
              </button>
            </div>
          </div>
        </div>

        {projectLoading && (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Icon name="Loader2" size={14} className="animate-spin" />
            {t('invoiceInbox.form.project.loading')}
          </div>
        )}

        {/* ─── Inline Expense Form ─────────────────────────── */}
        {documentType === 'expense' && selectedProjectId && !projectLoading && (
          <>
            {/* AI pre-fill hint */}
            {(expenseForm?.vendor || expenseForm?.amount) && (
              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <Icon name="Sparkles" size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-700 space-y-0.5">
                  {expenseForm?.vendor && <p><span className="font-medium">{t('invoiceInbox.form.ai.supplierLabel')}</span> {expenseForm?.vendor}</p>}
                  {expenseForm?.amount && <p><span className="font-medium">{t('invoiceInbox.form.ai.amountDetected')}</span> {fmtCAD(expenseForm?.amount)}</p>}
                  <p className="text-blue-500">{t('invoiceInbox.form.ai.fieldsPreFilled')}</p>
                </div>
              </div>
            )}

            {/* Vendor */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoiceInbox.form.expense.vendorLabel')} <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={expenseForm?.vendor}
                onChange={e => setExpenseForm(p => ({ ...p, vendor: e?.target?.value }))}
                placeholder={t('invoiceInbox.form.expense.vendorPlaceholder')}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>

            {/* Payment Request (DP) — mandatory */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('invoiceInbox.form.expense.dpLabel')} <span className="text-red-500">*</span>
              </label>
              <select
                value={expenseForm?.paymentRequestId || ''}
                onChange={e => setExpenseForm(p => ({ ...p, paymentRequestId: e?.target?.value }))}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white"
              >
                <option value="">{t('invoiceInbox.form.expense.dpNoneOption')}</option>
                {(paymentRequests || [])?.map(pr => (
                  <option key={pr?.id} value={pr?.id}>
                    {dpOptionLabel(pr, t, 'invoiceInbox.form.expense')}
                  </option>
                ))}
              </select>
            </div>

            {/* Date + Reference */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoiceInbox.form.expense.expenseDateLabel')}</label>
                <input
                  type="date"
                  value={expenseForm?.expenseDate}
                  onChange={e => setExpenseForm(p => ({ ...p, expenseDate: e?.target?.value }))}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoiceInbox.form.expense.referenceLabel')}</label>
                <input
                  type="text"
                  value={expenseForm?.referenceNumber}
                  onChange={e => setExpenseForm(p => ({ ...p, referenceNumber: e?.target?.value }))}
                  placeholder={t('invoiceInbox.form.expense.referencePlaceholder')}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoiceInbox.form.expense.amountLabel')} <span className="text-red-500">*</span></label>
              {/* Taxes incluses toggle */}
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  id="taxesIncluded"
                  checked={expenseForm?.taxesIncluded || false}
                  onChange={e => {
                    setExpenseForm(p => ({ ...p, taxesIncluded: e?.target?.checked }));
                  }}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="taxesIncluded" className="text-xs text-gray-600 cursor-pointer select-none">
                  {t('invoiceInbox.form.expense.taxesIncluded')}
                </label>
              </div>
              <input
                type="text"
                inputMode="decimal"
                value={expenseAmountEditing ? expenseForm?.amount : expenseForm?.amount ? fmtCAD(expenseForm?.amount) : ''}
                onFocus={() => setExpenseAmountEditing(true)}
                onBlur={() => {
                  setExpenseAmountEditing(false);
                }}
                onChange={e => {
                  setExpenseForm(p => ({ ...p, amount: e?.target?.value }));
                }}
                placeholder="0.00"
                className="w-full text-right text-xs border border-gray-200 rounded-lg px-2 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              {expenseForm?.taxesIncluded && parseFloat(expenseForm?.amount) > 0 && (
                <p className="mt-1 text-xs text-amber-600">
                  {t('invoiceInbox.form.expense.taxesIncludedHint')}
                </p>
              )}
            </div>

            {/* TPS + TVQ (auto-fill defaults, manually editable) */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoiceInbox.form.expense.tpsLabel')}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={moneyValue('expTps', expenseForm?.expTps)}
                  onFocus={() => setEditingMoney('expTps')}
                  onBlur={() => commitExpenseMoney('expTps')}
                  onChange={e => setExpenseForm(p => ({ ...p, expTps: e?.target?.value }))}
                  placeholder={t('invoiceInbox.form.expense.autoPlaceholder')}
                  className="w-full text-right text-xs border border-gray-200 rounded-lg px-2 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoiceInbox.form.expense.tvqLabel')}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={moneyValue('expTvq', expenseForm?.expTvq)}
                  onFocus={() => setEditingMoney('expTvq')}
                  onBlur={() => commitExpenseMoney('expTvq')}
                  onChange={e => setExpenseForm(p => ({ ...p, expTvq: e?.target?.value }))}
                  placeholder={t('invoiceInbox.form.expense.autoPlaceholder')}
                  className="w-full text-right text-xs border border-gray-200 rounded-lg px-2 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            {/* Total (auto-fill default, manually editable) */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoiceInbox.form.expense.totalLabel')}</label>
              <input
                type="text"
                inputMode="decimal"
                value={moneyValue('expTotal', expenseForm?.expTotal)}
                onFocus={() => setEditingMoney('expTotal')}
                onBlur={() => commitExpenseMoney('expTotal')}
                onChange={e => setExpenseForm(p => ({ ...p, expTotal: e?.target?.value }))}
                placeholder="0.00"
                className="w-full text-right text-xs font-bold border border-gray-200 rounded-lg px-2 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoiceInbox.form.expense.statusLabel')}</label>
              <div className="flex gap-0 border border-gray-200 rounded-lg overflow-hidden">
                {['paid', 'pending', 'approved']?.map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setExpenseForm(p => ({ ...p, status: s }))}
                    className={`flex-1 py-2 text-xs font-medium capitalize transition-colors ${
                      expenseForm?.status === s ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {t(`invoiceInbox.form.expense.status${s?.charAt(0)?.toUpperCase() + s?.slice(1)}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Division */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoiceInbox.form.expense.divisionLabel')}</label>
              {(() => {
                const constructionDivCodes = new Set(
                  (costCodes || [])
                    ?.filter(c => c?.category === 'construction')
                    ?.map(c => extractDivisionCode(c?.costCode))
                    ?.filter(Boolean)
                );
                const constructionDivisions = (divisions || [])?.filter(d => constructionDivCodes?.has(d?.code));
                const generalCodes = (costCodes || [])?.filter(c => c?.category === 'general');
                const adminCodes = (costCodes || [])?.filter(c => c?.category === 'admin');
                const groups = [
                  ...(constructionDivisions?.length ? [{
                    label: t('invoiceInbox.form.expense.groupConstruction'),
                    options: constructionDivisions?.map(d => ({ value: `construction::${d?.code}`, label: `${d?.code} — ${d?.name}` }))
                  }] : []),
                  ...(generalCodes?.length ? [{
                    label: t('invoiceInbox.form.expense.groupGeneral'),
                    options: generalCodes?.map(c => ({ value: `general::${c?.costCode}`, label: `${c?.costCode} — ${c?.description}` }))
                  }] : []),
                  ...(adminCodes?.length ? [{
                    label: t('invoiceInbox.form.expense.groupAdmin'),
                    options: adminCodes?.map(c => ({ value: `admin::${c?.costCode}`, label: `${c?.costCode} — ${c?.description}` }))
                  }] : []),
                ];
                return (
                  <GroupedSelect
                    groups={groups}
                    value={expenseForm?.divisionSelection}
                    onChange={val => {
                      const [cat, code] = (val || '')?.split('::');
                      if (!val) {
                        setExpenseForm(p => ({ ...p, divisionSelection: '', divisionCode: '', costCode: '' }));
                      } else if (cat === 'construction') {
                        setExpenseForm(p => ({ ...p, divisionSelection: val, divisionCode: code, costCode: '' }));
                      } else {
                        setExpenseForm(p => ({ ...p, divisionSelection: val, divisionCode: code, costCode: code }));
                      }
                    }}
                    placeholder={t('invoiceInbox.form.expense.selectDivision')}
                  />
                );
              })()}
            </div>

            {/* Code de coût (cascade) */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoiceInbox.form.expense.costCodeLabel')}</label>
              {(() => {
                const [selCat] = (expenseForm?.divisionSelection || '')?.split('::');
                if (selCat === 'construction') {
                  const filtered = (costCodes || [])?.filter(
                    c => c?.category === 'construction' && extractDivisionCode(c?.costCode) === expenseForm?.divisionCode
                  );
                  return (
                    <SearchableSelect
                      options={filtered?.map(c => ({ value: c?.costCode, label: `${c?.costCode} — ${c?.description}` }))}
                      value={expenseForm?.costCode}
                      onChange={val => setExpenseForm(p => ({ ...p, costCode: val || '' }))}
                      placeholder={t('invoiceInbox.form.expense.selectCostCodePlaceholder')}
                      disabled={!expenseForm?.divisionCode}
                    />
                  );
                } else if (selCat === 'general' || selCat === 'admin') {
                  return (
                    <input
                      type="text"
                      readOnly
                      value={expenseForm?.costCode || ''}
                      className="w-full text-xs border border-gray-100 rounded-lg px-3 py-2 bg-gray-50 text-gray-600 cursor-default focus:outline-none"
                    />
                  );
                } else {
                  return (
                    <SearchableSelect
                      options={[]}
                      value=""
                      onChange={() => {}}
                      placeholder={t('invoiceInbox.form.expense.selectDivisionFirst')}
                      disabled
                    />
                  );
                }
              })()}
            </div>

            {/* Extra / Crédit */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoiceInbox.form.expense.extraCreditLabel')} <span className="text-gray-400 font-normal">{t('invoiceInbox.form.expense.optionalSuffix')}</span></label>
              <SearchableSelect
                options={[...(extras || [])?.map(ex => ({ value: ex?.id, label: ex?.title }))]}
                value={expenseForm?.extraId}
                onChange={val => setExpenseForm(p => ({ ...p, extraId: val || '' }))}
                placeholder={t('invoiceInbox.form.invoice.noneOption')}
                allowNone
              />
            </div>

            {/* Payé par */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoiceInbox.form.expense.paidByLabel')}</label>
              <SearchableSelect
                options={[
                  { value: 'Visa', label: 'Visa' },
                  { value: 'Cheque', label: 'Cheque' },
                ]}
                value={expenseForm?.paidBy}
                onChange={val => setExpenseForm(p => ({ ...p, paidBy: val || '' }))}
                placeholder={t('invoiceInbox.form.expense.selectPlaceholder')}
                allowNone
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoiceInbox.form.expense.descriptionLabel')}</label>
              <textarea
                value={expenseForm?.description}
                onChange={e => setExpenseForm(p => ({ ...p, description: e?.target?.value }))}
                rows={2}
                placeholder={t('invoiceInbox.form.expense.descriptionPlaceholder')}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              />
            </div>
          </>
        )}

        {selectedProjectId && !projectLoading && documentType === 'invoice' && (
          <>
            {/* Invoice Type */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">{t('invoice.form.invoiceType')} <span className="text-red-500">*</span></label>
              <div className="flex gap-0 border border-gray-200 rounded-lg overflow-hidden">
                {[
                  { value: 'subcontract', label: t('invoice.form.subcontract') },
                  { value: 'extra', label: t('invoice.form.extra') },
                  { value: 'credit', label: t('invoice.form.credit') },
                ]?.map(type => (
                  <button
                    key={type?.value}
                    type="button"
                    onClick={() => setInvoiceType(type?.value)}
                    className={`flex-1 py-2 text-xs font-medium transition-colors ${
                      invoiceType === type?.value ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {type?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Subcontract */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('invoiceInbox.form.invoice.subcontractLabel')} <span className="text-red-500">*</span>
                {autoSelected?.subcontractor && (
                  <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                    <Icon name="Sparkles" size={10} />
                    {t('invoiceInbox.form.project.autoSelected')}
                  </span>
                )}
              </label>
              <SubcontractCombobox
                subcontracts={subcontracts || []}
                value={form?.subcontractId || null}
                onChange={val => { setForm(p => ({ ...p, subcontractId: val || '', contractId: invoiceType === 'subcontract' ? val || '' : p?.contractId })); setAutoSelected(prev => ({ ...prev, subcontractor: false })); }}
                placeholder={t('invoiceInbox.form.invoice.searchSubcontract')}
                noProjectSelected={!selectedProjectId}
                size="md"
              />
            </div>

            {invoiceType === 'extra' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoice.form.extra')} <span className="text-red-500">*</span></label>
                <SearchableSelect
                  options={extraOptions?.map(e => ({ value: e?.id, label: e?.title }))}
                  value={form?.extraId || null}
                  onChange={val => setField('extraId', val || '')}
                  placeholder={t('invoiceInbox.form.invoice.searchExtras')}
                  size="md"
                />
              </div>
            )}

            {invoiceType === 'credit' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoice.form.credit')} <span className="text-red-500">*</span></label>
                <SearchableSelect
                  options={creditOptions?.map(e => ({ value: e?.id, label: e?.title }))}
                  value={form?.creditId || null}
                  onChange={val => setField('creditId', val || '')}
                  placeholder={t('invoiceInbox.form.invoice.searchCredits')}
                  size="md"
                />
              </div>
            )}

            {/* Invoice Number + Payment Request (DP) */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoiceInbox.form.invoice.invoiceNumberLabel')}</label>
                <input
                  type="text"
                  value={form?.invoiceNumber}
                  onChange={e => setField('invoiceNumber', e?.target?.value)}
                  placeholder={t('invoiceInbox.form.invoice.invoiceNumberPlaceholder')}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('invoiceInbox.form.invoice.dpLabel')} <span className="text-red-500">*</span>
                </label>
                <select
                  value={form?.paymentRequestId || ''}
                  onChange={e => setField('paymentRequestId', e?.target?.value)}
                  className={`w-full text-xs border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white ${!form?.paymentRequestId && error === t('invoiceInbox.form.errors.paymentRequestRequired') ? 'border-red-400' : 'border-gray-200'}`}
                >
                  <option value="">{t('invoiceInbox.form.invoice.noneOption')}</option>
                  {(paymentRequests || [])?.map(pr => (
                    <option key={pr?.id} value={pr?.id}>
                      {dpOptionLabel(pr, t, 'invoiceInbox.form.invoice')}
                    </option>
                  ))}
                </select>
                {!form?.paymentRequestId && error === t('invoiceInbox.form.errors.paymentRequestRequired') && (
                  <p className="mt-1 text-xs text-red-500">{t('invoiceInbox.form.errors.paymentRequestRequired')}</p>
                )}
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoice.form.invoiceDate')}</label>
                <input
                  type="date"
                  value={form?.invoiceDate}
                  onChange={e => setField('invoiceDate', e?.target?.value)}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            {/* Invoice Lines */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-gray-700">{t('invoice.form.invoiceLines')}</label>
                <button onClick={addLine} className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary-dark font-medium">
                  <Icon name="Plus" size={12} /> {t('invoice.form.addLine')}
                </button>
              </div>
              <div className="space-y-2">
                {lines?.map((line, i) => (
                  <div key={i} className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
                    <div className="flex items-center gap-2">
                      <SearchableSelect
                        options={[
                          { value: 'cost_code', label: t('invoiceInbox.form.invoice.lineTypeCostCode') },
                          { value: 'extra_credit', label: t('invoiceInbox.form.invoice.lineTypeExtraCredit') },
                        ]}
                        value={line?.lineType}
                        onChange={val => setLine(i, 'lineType', val || 'cost_code')}
                        placeholder="Select type..."
                        size="sm"
                      />
                      {lines?.length > 1 && (
                        <button onClick={() => removeLine(i)} className="ml-auto p-1 rounded text-gray-400 hover:text-red-500">
                          <Icon name="Trash2" size={12} />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-[2fr_1fr] gap-3">
                      <div>
                        {line?.lineType === 'cost_code' ? (
                          <>
                            <SearchableSelect
                              options={costCodes?.map(c => ({ value: c?.id, label: `${c?.costCode} – ${c?.description}`, searchText: `${c?.costCode} ${c?.description}`?.toLowerCase() }))}
                              value={line?.projectCostId || null}
                              onChange={val => { setLine(i, 'projectCostId', val || ''); if (i === 0) setAutoSelected(prev => ({ ...prev, costCode: false })); }}
                              placeholder={t('invoice.form.selectCostCode')}
                              size="sm"
                            />
                            {i === 0 && autoSelected?.costCode && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 mt-1">
                                <Icon name="Sparkles" size={10} />
                                {t('invoiceInbox.form.project.autoSelected')}
                              </span>
                            )}
                          </>
                        ) : (
                          <SearchableSelect
                            options={extras?.map(ex => ({ value: ex?.id, label: ex?.title }))}
                            value={line?.extraCreditId || null}
                            onChange={val => setLine(i, 'extraCreditId', val || '')}
                            placeholder="Select extra/credit..."
                            size="sm"
                          />
                        )}
                      </div>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={amountDisplays?.[i] === 'editing' ? line?.amount : line?.amount ? fmtCAD(line?.amount) : ''}
                        onFocus={() => handleAmountFocus(i)}
                        onBlur={() => handleAmountBlur(i)}
                        onChange={e => setLine(i, 'amount', e?.target?.value)}
                        placeholder="0.00"
                        className="w-full text-right text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                    {line?.lineType === 'cost_code' && getLaborMaterialSubcontracts(line?.projectCostId)?.length > 0 && (
                      <div>
                        <label className="block text-[10px] font-medium text-gray-500 mb-1">{t('invoiceInbox.form.invoice.subcontractOptional')} <span className="text-gray-400">{t('invoiceInbox.form.invoice.subcontractOptionalSuffix')}</span></label>
                        <SearchableSelect
                          options={[
                            { value: '', label: t('invoiceInbox.form.invoice.noneOption') },
                            ...getLaborMaterialSubcontracts(line?.projectCostId)?.map(s => ({ value: s?.id, label: s?.name || s?.company || s?.id }))
                          ]}
                          value={line?.subcontractId || null}
                          onChange={val => setLine(i, 'subcontractId', val || '')}
                          placeholder={t('invoiceInbox.form.invoice.selectSubcontractOptional')}
                          size="sm"
                        />
                      </div>
                    )}
                    <input
                      type="text"
                      value={line?.description}
                      onChange={e => setLine(i, 'description', e?.target?.value)}
                      placeholder={t('invoiceInbox.form.invoice.descriptionOptional')}
                      className="w-full mt-2 text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Tax Summary */}
            <div className="border border-gray-100 rounded-lg p-3 bg-gray-50 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600">{t('invoice.form.subtotal')}</span>
                <span className="text-xs font-semibold text-gray-800">{fmtCAD(subtotal)}</span>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="invoiceFormApplyTax"
                  checked={applyTax}
                  onChange={e => setApplyTax(e?.target?.checked)}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer"
                />
                <label htmlFor="invoiceFormApplyTax" className="text-xs text-gray-600 cursor-pointer">{t('invoice.form.applyTaxes')}</label>
              </div>
              {applyTax && (
                <>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-500 whitespace-nowrap">TPS (5%)</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={moneyValue('invTps', invTps)}
                      onFocus={() => setEditingMoney('invTps')}
                      onBlur={() => commitInvoiceMoney(invTps, setInvTps)}
                      onChange={e => setInvTps(e?.target?.value)}
                      className="w-28 text-right text-xs border border-gray-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-500 whitespace-nowrap">TVQ (9.975%)</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={moneyValue('invTvq', invTvq)}
                      onFocus={() => setEditingMoney('invTvq')}
                      onBlur={() => commitInvoiceMoney(invTvq, setInvTvq)}
                      onChange={e => setInvTvq(e?.target?.value)}
                      className="w-28 text-right text-xs border border-gray-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                </>
              )}
              <div className="border-t border-gray-300 mt-1 pt-2 flex items-center justify-between gap-2">
                <span className="text-xs font-bold text-gray-900">{t('invoice.form.grandTotal')}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={moneyValue('invTotal', invTotal)}
                  onFocus={() => setEditingMoney('invTotal')}
                  onBlur={() => commitInvoiceMoney(invTotal, setInvTotal)}
                  onChange={e => setInvTotal(e?.target?.value)}
                  className="w-28 text-right text-xs font-bold border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            {/* Retention module — this becomes the FINAL invoice amount (net) */}
            <div className="border border-gray-100 rounded-lg p-3 bg-gray-50 space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="invoiceFormApplyRetention"
                  checked={retentionIncluded}
                  onChange={e => setRetentionIncluded(e?.target?.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer"
                />
                <label htmlFor="invoiceFormApplyRetention" className="text-xs font-medium text-gray-700 cursor-pointer">
                  {t('invoiceInbox.form.invoice.applyRetention')}
                </label>
              </div>
              {retentionIncluded && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoiceInbox.form.invoice.retentionPct')}</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={retentionPct}
                      onChange={e => setRetentionPct(e?.target?.value)}
                      className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>
                  <div className="bg-blue-50 rounded-lg border border-blue-100 p-3 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">{t('invoiceInbox.form.invoice.approvedAmount')}</span>
                      <span className="font-medium text-gray-900">{fmtCAD(fullTotal)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">{t('invoiceInbox.form.invoice.retentionLine', { pct: retentionPctNum })}</span>
                      <span className="font-medium text-red-600">−{fmtCAD(retentionAmount)}</span>
                    </div>
                    <div className="flex justify-between text-xs border-t border-blue-100 pt-1">
                      <span className="font-semibold text-gray-700">{t('invoiceInbox.form.invoice.netPayable')}</span>
                      <span className="font-bold text-green-700">{fmtCAD(netPayable)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('invoiceInbox.form.invoice.notesLabel')}</label>
              <textarea
                value={form?.notes}
                onChange={e => setField('notes', e?.target?.value)}
                rows={2}
                placeholder={t('invoiceInbox.form.invoice.notesPlaceholder')}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              />
            </div>
          </>
        )}
      </div>
      {/* Footer */}
      <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-gray-100 flex-shrink-0">
        {onCancel && (
          <button
            onClick={onCancel}
            className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {t('common.cancel')}
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={saving || !selectedProjectId}
          className={`px-4 py-2 text-xs font-medium text-white rounded-lg transition-colors disabled:opacity-50 ${
            documentType === 'expense' ?'bg-blue-500 hover:bg-blue-600' :'bg-primary hover:bg-primary-dark'
          }`}
        >
          {saving
            ? t('invoiceInbox.form.invoice.processingLabel')
            : documentType === 'expense' ? t('invoiceInbox.form.expense.createExpenseButton') : (submitLabel || t('invoiceInbox.form.createInvoice'))}
        </button>
      </div>
    </div>
  );
};

export default InvoiceForm;
